import { useState } from "react";
import { Box, Button, Text, Flex } from "@chakra-ui/react";
import { Cookie, X } from "lucide-react";
import { When } from "react-if";
import styled, { useTheme } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { AppTheme } from "../theme";
import { THEME } from "../helpers/constans";

const CookieContainer = styled(motion.div)<{ $theme: AppTheme }>`
  position: fixed;
  bottom: 5.5rem;
  left: 50%;
  max-width: 420px;
  width: calc(100% - 2rem);
  background: ${({ $theme }) => $theme.backgroundCard};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid ${({ $theme }) => $theme.border};
  border-radius: ${THEME.borderRadiusLg};
  padding: 1.25rem;
  box-shadow: ${({ $theme }) => $theme.shadowXl};
  z-index: 2000;
  margin-left: auto;
  margin-right: auto;
  transform: translateX(-50%);
`;

const CookieIconWrapper = styled.div<{ $theme: AppTheme }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${THEME.borderRadius};
  background: ${({ $theme }) => `${$theme.accent}15`};
  color: ${({ $theme }) => $theme.accent};
  margin-bottom: 1rem;
`;

const CloseButton = styled.button<{ $theme: AppTheme }>`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${THEME.borderRadiusFull};
  background: ${({ $theme }) => $theme.backgroundMuted};
  border: 1px solid ${({ $theme }) => $theme.border};
  color: ${({ $theme }) => $theme.textMuted};
  cursor: pointer;
  transition: all ${THEME.transitionFast};
  
  &:hover {
    background: ${({ $theme }) => $theme.backgroundMuted};
    color: ${({ $theme }) => $theme.text};
    transform: scale(1.1);
  }
`;

export function Cookies() {
  const theme = useTheme() as AppTheme;
  const [isCookieAccepted, setIsCookieAccepted] = useState<string | null>(
    () => {
      return localStorage.getItem("smieciarkajedzie_cookie_accepted");
    },
  );

  const onCookieAccept = () => {
    localStorage.setItem("smieciarkajedzie_cookie_accepted", "accepted");
    setIsCookieAccepted("accepted");
  };

  return (
    <When condition={isCookieAccepted !== "accepted"}>
      <AnimatePresence>
        <CookieContainer
          $theme={theme}
          initial={{ opacity: 0, y: 20, scale: 0.95, x: "-50%" }}
          animate={{ opacity: 1, y: 0, scale: 1, x: "-50%" }}
          exit={{ opacity: 0, y: 20, scale: 0.95, x: "-50%" }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <CloseButton
            $theme={theme}
            onClick={onCookieAccept}
            aria-label="Zamknij"
          >
            <X size={16} />
          </CloseButton>

          <Flex direction="column" align="center" textAlign="center">
            <CookieIconWrapper $theme={theme}>
              <Cookie size={24} />
            </CookieIconWrapper>

            <Text
              fontFamily="'Outfit', sans-serif"
              fontSize="lg"
              fontWeight={700}
              color={theme.text}
              mb={2}
              letterSpacing="-0.02em"
            >
              Chcesz ciasteczko? 🍪
            </Text>

            <Text
              fontSize="sm"
              color={theme.textSecondary}
              lineHeight="tall"
              mb={4}
              fontFamily="'Plus Jakarta Sans', sans-serif"
              fontWeight={500}
            >
              Korzystanie z witryny smieciarkajedzie.com oznacza zgodę na
              wykorzystywanie plików cookies, z których niektóre mogą być już
              zapisane w folderze przeglądarki.
            </Text>

            <Button
              onClick={onCookieAccept}
              background={theme.gradientPrimary}
              color="white"
              borderRadius={THEME.borderRadiusFull}
              px={6}
              py={2}
              fontWeight={600}
              fontSize="sm"
              fontFamily="'Plus Jakarta Sans', sans-serif"
              letterSpacing="0.02em"
              boxShadow={`0 4px 15px ${theme.primary}30`}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${theme.primary}40`,
              }}
              _active={{
                transform: "translateY(0)",
              }}
              transition="all 0.2s ease"
              width="100%"
            >
              Akceptuję
            </Button>
          </Flex>
        </CookieContainer>
      </AnimatePresence>
    </When>
  );
}
